/**
 * UPMI — bespoke About page (handoff Page 4): teal hero + pull-quote, story
 * two-column + stat row, mission/vision cards, USP numbered list, trust ribbon.
 */
import { Link } from 'react-router-dom';
import { Eye, Lock, ShieldCheck, Target, Truck, Wallet } from 'lucide-react';
import { UpmiShell } from './shell';
import { INK, LINE, MEDIA, MUTED, O, OGRAD, OL } from './tokens';

const STATS: [string, string][] = [
  ['10+', 'Years in wellness'],
  ['50k+', 'Members nationwide'],
  ['100%', 'Authentic products'],
];

const USP = [
  'High-quality, well-formulated products',
  'Scientifically-proven formulations approved for all skin types',
  'Customer-centric services that put a premium on consumer welfare',
  'Target-focused ingredients for the concerns of modern living',
  'Real business opportunities across a vast global network',
];

const TRUST: { Icon: typeof ShieldCheck; label: string }[] = [
  { Icon: ShieldCheck, label: '100% Authentic' },
  { Icon: Wallet, label: 'Earn PV + Wallet Cashback' },
  { Icon: Truck, label: 'Nationwide Shipping' },
  { Icon: Lock, label: 'Secure Checkout' },
];

export function UpmiAbout(): JSX.Element {
  return (
    <UpmiShell active="about">
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '20px 16px 64px' }}>
        <Breadcrumb trail={['Home', 'About Us']} />

        {/* Hero */}
        <section
          style={{
            background: OGRAD,
            borderRadius: 12,
            padding: '52px 48px',
            minHeight: 220,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '.2em',
              textTransform: 'uppercase',
              opacity: 0.85,
            }}
          >
            Ultra Proactive Marketing, Inc.
          </div>
          <h1
            style={{
              fontSize: 'clamp(28px,3.6vw,40px)',
              fontWeight: 800,
              margin: '12px 0 14px',
              letterSpacing: '-.01em',
            }}
          >
            About Ultra Proactive
          </h1>
          <p
            style={{
              fontStyle: 'italic',
              fontSize: 16,
              maxWidth: '52ch',
              lineHeight: 1.6,
              opacity: 0.95,
              margin: 0,
            }}
          >
            “This company provides top-of-the-line, quality products with a league of its own.”
          </p>
        </section>

        {/* Story */}
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1.15fr) minmax(0,1fr)',
            gap: 36,
            alignItems: 'center',
            marginTop: 46,
          }}
        >
          <div>
            <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 16px', color: INK }}>
              Beauty, wellness &amp; opportunity
            </h2>
            <p style={{ color: '#555', fontSize: 15, lineHeight: 1.75, margin: '0 0 24px' }}>
              Ultra Proactive Marketing, Inc. is a Filipino wellness company behind the flagship
              PROMAG300 Premier magnesium line. We pair scientifically-formulated products with a
              transparent, rewarding membership so every Filipino can look after their health and
              build a real income at the same time.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
              {STATS.map(([n, label]) => (
                <div
                  key={label}
                  style={{
                    textAlign: 'center',
                    background: OL,
                    borderRadius: 10,
                    padding: '18px 10px',
                  }}
                >
                  <div style={{ fontSize: 28, fontWeight: 800, color: O, lineHeight: 1 }}>{n}</div>
                  <div style={{ fontSize: 12.5, color: MUTED, marginTop: 6 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
          <div
            style={{ aspectRatio: '4 / 3', borderRadius: 12, overflow: 'hidden', background: OL }}
          >
            <img
              src={`${MEDIA}/about/reception.jpg`}
              alt="Ultra Proactive office"
              loading="lazy"
              decoding="async"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
        </section>

        {/* Mission & Vision */}
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
            gap: 20,
            marginTop: 46,
          }}
        >
          {[
            {
              Icon: Target,
              title: 'Our Mission',
              body: 'To enrich lives by delivering innovative, high-quality beauty and wellness solutions that inspire confidence, promote well-being, and create lasting value for our customers and partners.',
            },
            {
              Icon: Eye,
              title: 'Our Vision',
              body: 'To be the trusted leader in beauty and wellness, delivering innovative, high-quality products that inspire healthier lives and create lasting value for our customers, partners, and communities.',
            },
          ].map((c) => (
            <div
              key={c.title}
              style={{
                background: '#fff',
                border: `1px solid ${LINE}`,
                borderRadius: 12,
                padding: 30,
              }}
            >
              <span
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 12,
                  background: OL,
                  color: O,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 16,
                }}
              >
                <c.Icon size={26} />
              </span>
              <h3 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 10px', color: INK }}>
                {c.title}
              </h3>
              <p style={{ color: '#555', fontSize: 14.5, lineHeight: 1.7, margin: 0 }}>{c.body}</p>
            </div>
          ))}
        </section>

        {/* USP */}
        <section
          style={{
            background: '#fff',
            border: `1px solid ${LINE}`,
            borderRadius: 12,
            padding: '34px 30px',
            marginTop: 46,
          }}
        >
          <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 24px', color: INK }}>
            Unique Selling Proposition
          </h2>
          <ol
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))',
              gap: '18px 40px',
            }}
          >
            {USP.map((p, i) => (
              <li key={p} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <span
                  style={{
                    flex: 'none',
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    background: O,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: 15,
                  }}
                >
                  {i + 1}
                </span>
                <span style={{ fontSize: 15, color: '#444', lineHeight: 1.55, paddingTop: 5 }}>
                  {p}
                </span>
              </li>
            ))}
          </ol>
        </section>

        {/* Trust ribbon */}
        <section
          style={{
            background: '#fff',
            border: `1px solid ${LINE}`,
            borderRadius: 12,
            padding: '24px 20px',
            marginTop: 24,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            gap: 16,
          }}
        >
          {TRUST.map(({ Icon, label }) => (
            <div
              key={label}
              style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}
            >
              <Icon size={22} color={O} />
              <span style={{ fontSize: 14, fontWeight: 700, color: INK }}>{label}</span>
            </div>
          ))}
        </section>

        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <Link
            to="/signup"
            style={{
              background: O,
              color: '#fff',
              fontWeight: 800,
              fontSize: 14,
              padding: '14px 34px',
              borderRadius: 999,
              textDecoration: 'none',
            }}
          >
            Become a Member
          </Link>
        </div>
      </div>
    </UpmiShell>
  );
}

function Breadcrumb({ trail }: { trail: string[] }) {
  return (
    <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 18 }}>
      {trail.map((t, i) => (
        <span key={t}>
          {i > 0 && <span style={{ margin: '0 8px', color: '#ccc' }}>›</span>}
          {i === 0 ? (
            <Link to="/" style={{ color: MUTED, textDecoration: 'none' }}>
              {t}
            </Link>
          ) : (
            <span style={{ color: INK }}>{t}</span>
          )}
        </span>
      ))}
    </div>
  );
}
